/**
 * Logos des marques (jeu couleur officiel, 30/10/2026 : sources et licences dans le PR, `resources/images/brands/`),
 * pour tous les panneaux de recherche : accueil A / B (`home/home-search.ts`), autocomplétion du site
 * (`shared/autocomplete-factory.ts`, `marketplace/marketplace-header-autocomplete.ts`, `Components/Algolia/Autocomplete.vue`).
 * Une seule liste. Une marque sans logo (Pagani : son ovale est un trait illisible à 24 px) prend ses initiales.
 */
import alpine from "../../images/brands/alpine.png";
import astonMartin from "../../images/brands/aston-martin.svg";
import audi from "../../images/brands/audi.svg";
import bentley from "../../images/brands/bentley.svg";
import bmw from "../../images/brands/bmw.svg";
import bugatti from "../../images/brands/bugatti.svg";
import ferrari from "../../images/brands/ferrari.svg";
import ford from "../../images/brands/ford.svg";
import koenigsegg from "../../images/brands/koenigsegg.svg";
import lamborghini from "../../images/brands/lamborghini.svg";
import landRover from "../../images/brands/land-rover.svg";
import lotus from "../../images/brands/lotus.svg";
import mclaren from "../../images/brands/mclaren.svg";
import mercedes from "../../images/brands/mercedes-benz.svg";
import mini from "../../images/brands/mini.svg";
import porsche from "../../images/brands/porsche.png";
import rollsRoyce from "../../images/brands/rolls-royce.svg";
import volkswagen from "../../images/brands/volkswagen.svg";
import volvo from "../../images/brands/volvo.svg";

/** Par slug de marque (VehicleMake::slug). */
const LOGOS: Record<string, string> = {
    alpine,
    "aston-martin": astonMartin,
    audi,
    bentley,
    bmw,
    bugatti,
    ferrari,
    ford,
    koenigsegg,
    lamborghini,
    "land-rover": landRover,
    lotus,
    mclaren,
    mercedes,
    mini,
    porsche,
    "rolls-royce": rollsRoyce,
    volkswagen,
    volvo,
};

/** Désignations ou débuts de libellé qui ne donnent pas directement le slug. */
const ALIASES: Record<string, string> = {
    "mercedes-benz": "mercedes",
    "range-rover": "land-rover",
};

/** Slug d'une marque à partir d'un slug, d'une désignation (« Land Rover », « Mercedes-Benz ») ou d'un libellé. */
export function brandSlug(nameOrSlug: string): string {
    const slug = nameOrSlug
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

    return ALIASES[slug] ?? slug;
}

/** URL du logo couleur de la marque, ou null (initiales). */
export function brandLogo(nameOrSlug: string | null | undefined): string | null {
    if (!nameOrSlug) return null;

    return LOGOS[brandSlug(nameOrSlug)] ?? null;
}

/** Initiales d'une marque sans logo : « Pagani » → « PA », « Land Rover » → « LR ». Pagani : « P » (Alexis, 30/10). */
export function brandInitials(name: string): string {
    if (brandSlug(name) === "pagani") return "P";
    const words = name.trim().split(/[\s-]+/).filter(Boolean);

    return (words.length > 1 ? words[0][0] + words[1][0] : name.trim().slice(0, 2)).toUpperCase();
}

/** Marques mises en avant par l'autocomplétion du site avant la saisie (même ordre qu'avant). */
export const FEATURED_BRANDS: Array<{ label: string; img: string }> = ["Porsche", "Ferrari", "Audi", "Mercedes", "McLaren", "Lamborghini"].map(
    (label) => ({ label, img: brandLogo(label) as string }),
);
